import { z } from 'zod';
import {
	AugmentedRealityModels,
	GameAutomaticNavigationSchema,
	GameEndsWhenSchema,
	GameRoundAvailabilitySchema,
	GameRoundSchema,
	GameStartBySchema,
	GameTaskSchema,
	GPSSettingsSchema,
	TeamsSchema,
	type GameVariable,
} from './game';
import { MediaNodeSchema } from './media';
import {
	AutomaticValidationKindSchema,
	GameMasterValidationKindSchema,
	GameTaskAvailabilitySchema,
	GameTaskTypeSchema,
	type AugmentedReality,
	type AutomaticValidation,
	type Game,
	type GameMasterValidation,
	type GameRound,
	type GameTask,
	// type QrSettings,
} from './game';

export const StringOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: z.string().default(''),
	})
	.default({ override: false, value: '' });
export type StringOverride = z.infer<typeof StringOverrideSchema>;

export const NumberOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: z.number().default(0),
	})
	.default({ override: false, value: 0 });
export type NumberOverride = z.infer<typeof NumberOverrideSchema>;

export const BooleanOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: z.boolean().default(false),
	})
	.default({ override: false, value: false });
export type BooleanOverride = z.infer<typeof BooleanOverrideSchema>;

export const MediaNodeOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: MediaNodeSchema.optional(),
	})
	.default({ override: false, value: undefined });
export type MediaNodeOverride = z.infer<typeof MediaNodeOverrideSchema>;

export const GPSSettingsOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: GPSSettingsSchema.optional(),
	})
	.default({ override: false, value: undefined });
export type GPSSettingsOverride = z.infer<typeof GPSSettingsOverrideSchema>;

// export const QrSettingsOverrideSchema = z
// 	.object({
// 		payload: StringOverrideSchema,
// 	})
// 	.default({});
// export type QrSettingsOverride = z.infer<typeof QrSettingsOverrideSchema>;

export const GameTaskAvailabilityOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: GameTaskAvailabilitySchema,
	})
	.default({ override: false, value: 'always' });
export type GameTaskAvailabilityOverride = z.infer<typeof GameTaskAvailabilityOverrideSchema>;

export const GameTaskTypeOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: GameTaskTypeSchema,
	})
	.default({ override: false, value: 'validation-by-game-master' });
export type GameTaskTypeOverride = z.infer<typeof GameTaskTypeOverrideSchema>;

export const GameMasterValidationKindOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: GameMasterValidationKindSchema,
	})
	.default({ override: false, value: 'open' });
export type GameMasterValidationKindOverride = z.infer<typeof GameMasterValidationKindOverrideSchema>;

export const GameMasterValidationOverrideSchema = z
	.object({
		kind: GameMasterValidationKindOverrideSchema,
	})
	.default({ kind: { override: false, value: 'open' } });
export type GameMasterValidationOverride = z.infer<typeof GameMasterValidationOverrideSchema>;

export const OpenAnswerOverrideSchema = z.object({
	id: z.string().uuid(),
	name: StringOverrideSchema,
});

export const MultipleChoiceAnswerOverrideSchema = z.object({
	id: z.string().uuid(),
	name: StringOverrideSchema,
	correct: BooleanOverrideSchema,
});

export const AutomaticValidationKindOverrideSchema = z.object({
	override: z.boolean().default(false),
	value: AutomaticValidationKindSchema,
});
export type AutomaticValidationKindOverride = z.infer<typeof AutomaticValidationKindOverrideSchema>;

export const AutomaticValidationOverrideSchema = z
	.object({
		kind: AutomaticValidationKindOverrideSchema,
		openAnswers: z.array(OpenAnswerOverrideSchema).optional(),
		openMarginForError: NumberOverrideSchema,
		multipleChoiceAnswers: z.array(MultipleChoiceAnswerOverrideSchema).optional(),
	})
	.default({
		kind: { override: false, value: 'open' },
		openAnswers: [],
		openMarginForError: { override: false, value: 0 },
		multipleChoiceAnswers: [],
	});
export type AutomaticValidationOverride = z.infer<typeof AutomaticValidationOverrideSchema>;

export const InformationOverrideSchema = z
	.object({
		content: StringOverrideSchema,
	})
	.default({});
export type InformationOverride = z.infer<typeof InformationOverrideSchema>;

export const AugmentedRealityModelOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: AugmentedRealityModels,
	})
	.default({ override: false, value: 'pagoda' });

export const AugmentedRealityOverrideSchema = z
	.object({
		model: AugmentedRealityModelOverrideSchema,
	})
	.default({ model: { override: false, value: 'pagoda' } });

export type AugmentedRealityOverride = z.infer<typeof AugmentedRealityOverrideSchema>;
export const GameTaskOverrideSchema = z.object({
	disabled: z.boolean().default(false),

	id: z.string().uuid(),
	title: StringOverrideSchema,
	subtitle: StringOverrideSchema,
	description: StringOverrideSchema,
	defaultCompletionResponse: StringOverrideSchema,
	defaultCompletionResponseWrong: StringOverrideSchema,
	attempts: NumberOverrideSchema,
	points: NumberOverrideSchema,
	media: MediaNodeOverrideSchema,
	autoplayMedia: BooleanOverrideSchema,
	gameMasterInstructions: StringOverrideSchema,

	availability: GameTaskAvailabilityOverrideSchema,
	// NOTE: all availability configs may be saved in a task schema so no data is lost when you switch from one availability method to the other
	// when you have a config for a different strategy than is set in availability it simply goes unused

	// used if availability == 'gps'
	availabilityGpsConfig: GPSSettingsOverrideSchema,
	// used if availability == 'qr'
	// availabilityQrConfig: QrSettingsOverrideSchema,
	// used if availability == 'task-completed': id of the task that should be completed to make this one available
	availabilityTaskToComplete: StringOverrideSchema,

	type: GameTaskTypeOverrideSchema,
	typeGameMasterValidationConfig: GameMasterValidationOverrideSchema,
	typeAutomaticValidationConfig: AutomaticValidationOverrideSchema,
	typeInformationConfig: InformationOverrideSchema,
	typeAugmentedRealityConfig: AugmentedRealityOverrideSchema,
});
export type GameTaskOverride = z.infer<typeof GameTaskOverrideSchema>;

export const GameRoundAvailabilityOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: GameRoundAvailabilitySchema,
	})
	.catch({ override: false, value: 'always' });
export type GameRoundAvailabilityOverride = z.infer<typeof GameRoundAvailabilityOverrideSchema>;

export const GameRoundOverrideSchema = z.object({
	disabled: z.boolean().default(false),

	id: z.string().uuid(),

	title: StringOverrideSchema,
	availability: GameRoundAvailabilityOverrideSchema,

	availabilityRoundToComplete: StringOverrideSchema,
	availabilityTimeValue: NumberOverrideSchema,
	availabilityGpsConfig: GPSSettingsOverrideSchema,
	// availabilityQrConfig: QrSettingsOverrideSchema,

	tasks: z.array(GameTaskOverrideSchema).default([]),
	extraTasks: z.array(GameTaskSchema).default([]),
});

export type GameRoundOverride = z.infer<typeof GameRoundOverrideSchema>;

export const GameVariablesOverrideSchema = z.object({
	key: z.string(),
	// key: z.custom<string>((val) => (typeof val === 'string' ? /^[a-z]+$/i.test(val) : false)).transform((e) => e.toUpperCase()),
	value: StringOverrideSchema,
});

export type GameVariableOverride = z.infer<typeof GameVariablesOverrideSchema>;

export const GameAutomaticNavigationOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: GameAutomaticNavigationSchema,
	})
	.default({ override: false, value: 'none' });
export type GameAutomaticNavigationOverride = z.infer<typeof GameAutomaticNavigationOverrideSchema>;

export const GameStartByOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: GameStartBySchema,
	})
	.default({ override: false, value: 'always-available' });
export type GameStartByOverride = z.infer<typeof GameStartByOverrideSchema>;

export const GameEndsWhenOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: GameEndsWhenSchema,
	})
	.default({ override: false, value: 'never' });
export type GameEndsWhenOverride = z.infer<typeof GameEndsWhenOverrideSchema>;

export const TeamsOverrideSchema = z
	.object({
		override: z.boolean().default(false),
		value: TeamsSchema,
	})
	.default({ override: false, value: 'default' });
export type TeamsOverride = z.infer<typeof TeamsOverrideSchema>;

export const GameOverrideSchema = z
	.object({
		title: StringOverrideSchema,
		subtitle: StringOverrideSchema,
		description: StringOverrideSchema,
		help: StringOverrideSchema,
		skin: StringOverrideSchema,

		automaticNavigation: GameAutomaticNavigationOverrideSchema,
		gameStartBy: GameStartByOverrideSchema,
		gameEndsWhen: GameEndsWhenOverrideSchema,
		duration: NumberOverrideSchema,
		teams: TeamsOverrideSchema,
		showMap: BooleanOverrideSchema,
		spectateFunction: BooleanOverrideSchema,
		globalChat: BooleanOverrideSchema,
		hideScore: BooleanOverrideSchema,
		gameMasterChat: BooleanOverrideSchema,
		rounds: z.array(GameRoundOverrideSchema).default([]),
		extraRounds: z.array(GameRoundSchema).default([]),
		variables: z.array(GameVariablesOverrideSchema).default([]),
	})
	.default({});

export type GameOverride = z.infer<typeof GameOverrideSchema>;

// for now event may only override variables
// but let's make it an object schema so we may add more fields later
export const GameOverrideEventSchema = z.object({
	variables: z.array(GameVariablesOverrideSchema).default([]),
	gameStartsByDate: z.coerce
		.date()
		.transform((d) => d.toISOString().split('T')[0])
		.optional(),
});
export type GameOverrideEvent = z.infer<typeof GameOverrideEventSchema>;

// TODO: game overrides
export const VariantSchema = z.object({
	gameId: z.string().uuid(),
	name: z.string().default(''),
	gameOverrides: GameOverrideSchema,
});
export type Variant = z.infer<typeof VariantSchema>;

export const VariantRecordSchema = z.object({
	id: z.string().uuid(),
	gameId: z.string().uuid(),
	variant: VariantSchema,
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
});
export type VariantRecord = z.infer<typeof VariantRecordSchema>;

export const VariantRecordUpdateSchema = z.object({
	id: z.string().uuid(),
	gameId: z.string().uuid(),
	variant: VariantSchema,
});
export type VariantRecordUpdate = z.infer<typeof VariantRecordUpdateSchema>;

export function applyGameEventOverrides(game: Game, overrides: GameOverrideEvent): Game {
	const appliedGame = {
		...game,
		variables: game.variables.map((variable) => {
			const variableOverride = overrides.variables.find((v) => v.key === variable.key);
			if (variableOverride) {
				return applyGameVariableOverride(variable, variableOverride);
			}
			return variable;
		}),
		gameStartsByDate: overrides.gameStartsByDate,
	};
	return appliedGame as Game;
}

/**
 * This function applies the overrides to the game.
 * It does this by recursively walking the tree of game properties and following the logic of: `if override then overrideValue else gameValue`
 *
 * For arrays the entries of the original game are preserved but the override may disable a given entry or change properties for an entry.
 * As of now overrides will never *add* new array entries.
 */
export function applyGameOverrides(game: Game, overrides: GameOverride): Game {
	const appliedGame = {
		...game,
		title: applyValueOverride(game.title, overrides.title),
		subtitle: applyValueOverride(game.subtitle, overrides.subtitle),
		description: applyValueOverride(game.description, overrides.description),
		help: applyValueOverride(game.help, overrides.help),
		skin: applyValueOverride(game.skin, overrides.skin),
		automaticNavigation: applyValueOverride(game.automaticNavigation, overrides.automaticNavigation),
		gameStartBy: applyValueOverride(game.gameStartBy, overrides.gameStartBy),
		gameEndsWhen: applyValueOverride(game.gameEndsWhen, overrides.gameEndsWhen),
		duration: applyValueOverride(game.duration, overrides.duration),
		teams: applyValueOverride(game.teams, overrides.teams),
		showMap: applyValueOverride(game.showMap, overrides.showMap),
		spectateFunction: applyValueOverride(game.spectateFunction, overrides.spectateFunction),
		globalChat: applyValueOverride(game.globalChat, overrides.globalChat),
		gameMasterChat: applyValueOverride(game.gameMasterChat, overrides.gameMasterChat),
		rounds: game.rounds
			.map((round) => {
				const roundOverride = overrides.rounds.find((r) => r.id === round.id);
				if (roundOverride && roundOverride.disabled) {
					return undefined;
				}
				return roundOverride ? applyGameRoundOverride(round, roundOverride) : round;
			})
			.filter((r) => r !== undefined)
			.concat(overrides.extraRounds),
		variables: game.variables.map((variable) => {
			const variableOverride = overrides.variables.find((v) => v.key === variable.key);
			if (variableOverride) {
				const appliedVar = applyGameVariableOverride(variable, variableOverride);
				return appliedVar;
			}
			return variable;
		}),
	};
	return appliedGame;
}

export function applyValueOverride<T>(value: T, override: { override: boolean; value?: T }): T {
	if (override.override) {
		return override.value as T;
	}
	return value;
}

function applyGameRoundOverride(round: GameRound, overrides: GameRoundOverride): GameRound {
	const appliedRound = {
		...round,
		title: applyValueOverride(round.title, overrides.title),
		availability: applyValueOverride(round.availability, overrides.availability),
		availabilityRoundToComplete: applyValueOverride(round.availabilityRoundToComplete, overrides.availabilityRoundToComplete),
		availabilityTimeValue: applyValueOverride(round.availabilityTimeValue, overrides.availabilityTimeValue),
		availabilityGpsConfig: applyValueOverride(round.availabilityGpsConfig, overrides.availabilityGpsConfig),
		// availabilityQrConfig: applyQrSettingsOverride(rounds.availabilityQrConfig, overrides.availabilityQrConfig),
		tasks: round.tasks
			.map((task) => {
				const taskOverride = overrides.tasks.find((t) => t.id === task.id);
				if (taskOverride && taskOverride.disabled) {
					return undefined;
				}
				return taskOverride ? applyGameTaskOverride(task, taskOverride) : task;
			})
			.filter((t) => t !== undefined)
			.concat(overrides.extraTasks),
	};
	return appliedRound;
}
function applyGameVariableOverride(variables: GameVariable, overrides: GameVariableOverride) {
	const appliedVariable = {
		key: variables.key,
		// for base games the variable may be left empty, but for variants the variable is required.
		value: applyValueOverride(variables.value, overrides.value) as string,
	};
	return appliedVariable;
}

function applyGameTaskOverride(task: GameTask, overrides: GameTaskOverride): GameTask {
	const appliedTask = {
		id: task.id,
		title: applyValueOverride(task.title, overrides.title),
		subtitle: applyValueOverride(task.subtitle, overrides.subtitle),
		description: applyValueOverride(task.description, overrides.description),
		defaultCompletionResponse: applyValueOverride(task.defaultCompletionResponse, overrides.defaultCompletionResponse),
		defaultCompletionResponseWrong: applyValueOverride(task.defaultCompletionResponseWrong, overrides.defaultCompletionResponseWrong),
		attempts: applyValueOverride(task.attempts, overrides.attempts),
		points: applyValueOverride(task.points, overrides.points),
		media: applyValueOverride(task.media, overrides.media),
		autoplayMedia: applyValueOverride(task.autoplayMedia, overrides.autoplayMedia),
		gameMasterInstructions: applyValueOverride(task.gameMasterInstructions, overrides.gameMasterInstructions),
		availability: applyValueOverride(task.availability, overrides.availability),
		availabilityGpsConfig: applyValueOverride(task.availabilityGpsConfig, overrides.availabilityGpsConfig),
		// availabilityQrConfig: applyQrSettingsOverride(task.availabilityQrConfig, overrides.availabilityQrConfig),
		availabilityTaskToComplete: applyValueOverride(task.availabilityTaskToComplete, overrides.availabilityTaskToComplete),
		type: applyValueOverride(task.type, overrides.type),

		// NOTE: type cast because the schema is defined as key-optional (foo?:T), but the types here return value-optional (foo:T|undefined)
		// so really the types are wrong but effectively the result is the same
		typeGameMasterValidationConfig: applyGameMasterValidationOverride(
			task.typeGameMasterValidationConfig,
			overrides.typeGameMasterValidationConfig,
		) as GameMasterValidation,
		typeAutomaticValidationConfig: applyAutomaticValidationOverride(
			task.typeAutomaticValidationConfig,
			overrides.typeAutomaticValidationConfig,
		) as AutomaticValidation,
		typeAugmentedRealityConfig: applyAugmentedRealityOverride(
			task.typeAugmentedRealityConfig,
			overrides.typeAugmentedRealityConfig,
		) as AugmentedReality,
	};
	return appliedTask;
}

function applyGameMasterValidationOverride(value: GameMasterValidation | undefined, override: GameMasterValidationOverride) {
	const newValidation = {
		kind: applyValueOverride(value?.kind, override.kind),
	};
	return newValidation;
}

function applyAutomaticValidationOverride(value: AutomaticValidation | undefined, override: AutomaticValidationOverride) {
	const newValidation: AutomaticValidation = {
		kind: applyValueOverride(value?.kind ?? 'open', override.kind),
		openMarginForError: applyValueOverride(value?.openMarginForError, override.openMarginForError) ?? 0,
		openAnswers: value?.openAnswers?.map((answer) => {
			const overrideName = override.openAnswers?.find((a) => a.id === answer.id)?.name;
			return {
				id: answer.id,
				name: overrideName?.override ? overrideName.value : answer.name,
			};
		}),
		multipleChoiceAnswers: value?.multipleChoiceAnswers?.map((answer) => {
			const or = override.multipleChoiceAnswers?.find((a) => a.id === answer.id);
			return {
				id: answer.id,
				name: or?.name?.override ? or.name.value : answer.name,
				correct: or?.correct?.override ? or.correct.value : answer.correct,
			};
		}),
	};
	return newValidation;
}

function applyAugmentedRealityOverride(value: AugmentedReality | undefined, override: AugmentedRealityOverride) {
	const newAugmentedReality = { model: applyValueOverride(value?.model, override.model) };
	return newAugmentedReality;
}
